import { sortParam } from '../sort.js';
import { z } from 'zod';
import { paginationQuery } from '../api.js';
import { nullableDate, nullableText, optionalDate, optionalText } from '../common.js';
import { offerLineInput } from '../offers/offers.js';
import { berlinDate } from '../calendar/dates.js';
/**
 * Verträge des Moduls Aufträge (KON-035). Ein Auftrag ist ein angenommenes Angebot oder ein direkt abgeschlossener Vertrag. Positionen und Beträge folgen
 * denselben Regeln wie im Angebot (ganze Cent, Mengen in Tausendsteln, Rabatte in Basispunkten), gerechnet wird mit offerTotals.
 */
/** Stände eines Auftrags (KON-127). „invoiced“ setzt später das Rechnungsmodul, von Hand ist er nicht wählbar. */
export const ORDER_STATUSES = ['open', 'planned', 'in_progress', 'done', 'cancelled', 'invoiced'];
export const ORDER_STATUS_LABELS = {
    open: 'Offen',
    planned: 'Geplant',
    in_progress: 'In Arbeit',
    done: 'Erledigt',
    cancelled: 'Storniert',
    invoiced: 'Abgerechnet',
};
/** Zwischen diesen Ständen wechselt das Büro frei */
export const ORDER_WORK_STATUSES = ['open', 'planned', 'in_progress', 'done'];
/** Gesperrt für Änderungen am Inhalt: abgerechnet (endgültig) und storniert (bis wieder geöffnet) */
export const orderEditable = (status) => status !== 'invoiced' && status !== 'cancelled';
/** Höchstzahl der Dateien je Auftrag (KON-129) */
export const ORDER_FILE_LIMIT = 30;
const lines = z.array(offerLineInput).min(1, 'Ein Auftrag braucht mindestens eine Position.').max(200, 'Höchstens 200 Positionen');
const basisPoints = z.number().int().min(0, 'Rabatt mindestens 0 %').max(10000, 'Rabatt höchstens 100 %');
export const createOrderInput = z
    .object({
    contactId: z.uuid('Bitte einen Kunden wählen'),
    inquiryId: z.uuid().optional(),
    subject: z.string().trim().min(2, 'Betreff fehlt').max(200),
    customerReference: optionalText(100),
    serviceStart: optionalDate,
    serviceEnd: optionalDate,
    notes: optionalText(10000),
    discountBp: basisPoints.default(0),
    lines,
})
    .strict();
export const updateOrderInput = z
    .object({
    contactId: z.uuid('Bitte einen Kunden wählen').optional(),
    subject: z.string().trim().min(2, 'Betreff fehlt').max(200).optional(),
    customerReference: nullableText(100),
    serviceStart: nullableDate,
    serviceEnd: nullableDate,
    notes: nullableText(10000),
    discountBp: basisPoints.optional(),
    /** Ersetzt alle Positionen */
    lines: lines.optional(),
})
    .strict()
    .refine((v) => Object.values(v).some((x) => x !== undefined), 'Keine Änderung angegeben');
/** Standwechsel. Zum Stornieren gehört ein Grund. */
export const changeOrderStatusInput = z
    .object({
    status: z.enum([...ORDER_WORK_STATUSES, 'cancelled']),
    reason: optionalText(1000),
})
    .strict()
    .refine((v) => v.status !== 'cancelled' || !!v.reason, { message: 'Bitte geben Sie einen Grund für die Stornierung an.', path: ['reason'] });
export const listOrdersQuery = paginationQuery.extend({
    q: z.string().trim().max(100).optional(),
    status: z.enum(ORDER_STATUSES).optional(),
    contactId: z.uuid().optional(),
    offerId: z.uuid().optional(),
    taskId: z.uuid().optional(),
    sort: sortParam(['number', 'contact', 'status', 'service', 'gross', 'createdAt'], '-createdAt'),
});
export const linkOrderTaskInput = z.object({ taskId: z.uuid('Bitte eine Aufgabe wählen') }).strict();
const dayText = (iso) => iso.split('-').reverse().join('.');
/**
 * Meldet Aufträge, bei denen Stand und Leistungsdatum nicht zusammenpassen: Ein Auftrag, der „Offen“ oder „Geplant“ ist, obwohl sein Leistungsdatum vorbei ist, wurde nicht gestartet. Ein Auftrag
 * „In Arbeit“, dessen Leistungsende vorbei ist, läuft noch, obwohl er längst zu Ende sein sollte. Das Büro soll dann den Stand setzen (In Arbeit, Erledigt, Storniert) oder das Datum ändern.
 * Erledigte, abgerechnete und stornierte Aufträge sind nie überfällig. `today` ist der heutige Tag in deutscher Zeit (JJJJ-MM-TT).
 */
export function orderOverdue(o, today = berlinDate()) {
    let due = null;
    let what = '';
    if (o.status === 'open' || o.status === 'planned') {
        due = o.serviceStart ?? o.serviceEnd;
        what = o.status === 'planned' ? 'Der Auftrag steht noch auf „Geplant“, wurde aber nicht gestartet.' : 'Der Auftrag steht noch auf „Offen“ und wurde nicht ausgeführt.';
    }
    else if (o.status === 'in_progress') {
        due = o.serviceEnd ?? o.serviceStart;
        what = 'Der Auftrag steht noch auf „In Arbeit“, obwohl das Leistungsende vorbei ist.';
    }
    if (!due || due >= today)
        return null;
    const days = Math.round((Date.parse(`${today}T00:00:00Z`) - Date.parse(`${due}T00:00:00Z`)) / 86_400_000);
    const ago = days === 1 ? 'seit gestern' : `seit ${days} Tagen`;
    return { days, short: ago, text: `Das Leistungsdatum (${dayText(due)}) liegt ${days === 1 ? 'einen Tag' : `${days} Tage`} zurück. ${what} Bitte setzen Sie den Stand (In Arbeit, Erledigt oder Stornieren) oder ändern Sie das Datum.` };
}
//# sourceMappingURL=orders.js.map